/** Data-driven vehicle fleet — add a vehicle by appending to VEHICLES. */

export type VehicleId =
  | "dash"
  | "bulldog"
  | "falcon"
  | "magneto"
  | "wasp"
  | "phantom";

export type PowerId =
  | "none"
  | "crush_crates"
  | "glider"
  | "magnet"
  | "phase";

export type VehiclePower = {
  id: PowerId;
  type: "none" | "passive" | "active";
  /** Active power duration in seconds. */
  duration?: number;
  /** Active power cooldown in seconds. */
  cooldown?: number;
  blurb: string;
};

export type VehicleDef = {
  id: VehicleId;
  name: string;
  blurb: string;
  price: number;
  /** "procedural" = hoverbike mesh; otherwise public GLB path. */
  model: "procedural" | string;
  color: string;
  emissive: string;
  trail: string;
  /** Display bars 1–5 in the garage. */
  stats: { speed: number; jump: number; coin: number };
  /** Gameplay multipliers applied on top of difficulty. */
  speedMult: number;
  jumpMult: number;
  coinMult: number;
  power: VehiclePower;
  /** Only unlockable via Starter Pack (unless already owned). */
  iapExclusive?: boolean;
};

export const VEHICLES: VehicleDef[] = [
  {
    id: "dash",
    name: "Dash Rider",
    blurb: "Stock hoverbike. Balanced and free.",
    price: 0,
    model: "procedural",
    color: "#22D3EE",
    emissive: "#0891B2",
    trail: "#67E8F9",
    stats: { speed: 3, jump: 3, coin: 3 },
    speedMult: 1,
    jumpMult: 1,
    coinMult: 1,
    power: { id: "none", type: "none", blurb: "No special power." },
  },
  {
    id: "bulldog",
    name: "Bulldog",
    blurb: "Heavy chassis. Slow, but crates don't stand a chance.",
    price: 400,
    model: "/models/low_poly_car_1.glb",
    color: "#F97316",
    emissive: "#C2410C",
    trail: "#FDBA74",
    stats: { speed: 2, jump: 2, coin: 3 },
    speedMult: 0.9,
    jumpMult: 0.9,
    coinMult: 1,
    power: {
      id: "crush_crates",
      type: "passive",
      blurb: "Crush crates on impact. Barriers & rivals still hurt.",
    },
  },
  {
    id: "falcon",
    name: "Falcon",
    blurb: "Light frame built for air time.",
    price: 750,
    model: "/models/low_poly_car_4.glb",
    color: "#34D399",
    emissive: "#047857",
    trail: "#6EE7B7",
    stats: { speed: 3, jump: 5, coin: 2 },
    speedMult: 1,
    jumpMult: 1.6,
    coinMult: 0.95,
    power: {
      id: "glider",
      type: "passive",
      blurb: "Glider — jump lasts +60% longer & higher.",
    },
  },
  {
    id: "magneto",
    name: "Magneto",
    blurb: "Coin vacuum for long farming runs.",
    price: 1200,
    model: "/models/low_poly_car_6.glb",
    color: "#E879F9",
    emissive: "#A21CAF",
    trail: "#F0ABFC",
    stats: { speed: 3, jump: 3, coin: 5 },
    speedMult: 1,
    jumpMult: 1,
    coinMult: 1.25,
    power: {
      id: "magnet",
      type: "active",
      duration: 6,
      cooldown: 20,
      blurb: "Coin magnet for 6s. Cooldown 20s.",
    },
  },
  {
    id: "wasp",
    name: "Wasp",
    blurb: "Stinger frame. Faster, snappier, no tricks.",
    price: 550,
    model: "procedural",
    color: "#FACC15",
    emissive: "#CA8A04",
    trail: "#FEF08A",
    stats: { speed: 4, jump: 3, coin: 2 },
    speedMult: 1.08,
    jumpMult: 1,
    coinMult: 0.9,
    power: { id: "none", type: "none", blurb: "No special power — just pace." },
  },
  {
    id: "phantom",
    name: "Phantom GT",
    blurb: "Top speed. Ghost through anything — briefly.",
    price: 2500,
    model: "/models/super_car.glb",
    color: "#FBBF24",
    emissive: "#B45309",
    trail: "#FDE68A",
    stats: { speed: 5, jump: 3, coin: 3 },
    speedMult: 1.15,
    jumpMult: 1,
    coinMult: 1,
    power: {
      id: "phase",
      type: "active",
      duration: 3,
      cooldown: 30,
      blurb: "Phase for 3s — pass through all obstacles. Cooldown 30s.",
    },
    iapExclusive: true,
  },
];

export const VEHICLE_BY_ID: Record<VehicleId, VehicleDef> = Object.fromEntries(
  VEHICLES.map((v) => [v.id, v]),
) as Record<VehicleId, VehicleDef>;

export const DEFAULT_VEHICLE_ID: VehicleId = "dash";
export const STARTER_PACK_VEHICLE: VehicleId = "phantom";

/** Magnet pull radius while power is active (world units). */
export const MAGNET_RADIUS = 4.2;

export function isVehicleId(id: unknown): id is VehicleId {
  return typeof id === "string" && id in VEHICLE_BY_ID;
}
